/* =========================================================================
   Seamless Pattern Maker — assets/seamless-pattern-maker.js (bearly.art)
   Runs on the Bearly Tools shell (sections/seamless-pattern-maker.liquid).
   Starter artwork comes from assets/seamless-pattern-maker-art.js (window.PM_STARTER).

   How it works: the user edits one tile in the middle of the canvas. Every element is
   drawn with "wrap copies" (its position shifted by the repeat's lattice), clipped to
   the tile, so anything that runs off one edge comes back on the opposite side. The
   area around the tile is the same tile repeated through an SVG <pattern>, so drags
   stay fast however many repeats are on screen. Preview draws every copy unclipped
   (seam-free) because it is a still view.

   GA4 + Klaviyo: through the shared core (BT.track / BT.klaviyoEvent), prefix from the
   metaobject (pm). Download popup + email gate: BT.runWithProgress (the ONE shared
   popup); pm_download_click + "Used Seamless Pattern Maker" fire inside `work`,
   after the file is saved.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.getElementById('pm-root');
  if (!root) return;
  var $ = function (id) { return document.getElementById(id); };

  var TOOL_NAME = 'Seamless Pattern Maker';
  function T(name, params) { try { if (window.BT && BT.track) BT.track(name, params); } catch (e) {} }
  function K(name, props) { try { if (window.BT && BT.klaviyoEvent) BT.klaviyoEvent(name, props); } catch (e) {} }
  function toast(msg) { if (window.BT && BT.toast) BT.toast(msg); }
  function syncRange(r) { try { if (window.BT && BT.syncRange) BT.syncRange(r); } catch (e) {} }

  var UPI = 100; // internal units per inch
  var f = function (n) { return Math.round(n * 1000) / 1000; };
  var ms = function (m) { return 'matrix(' + m.map(f).join(' ') + ')'; };
  var ap = function (M, x, y) { return [M[0] * x + M[2] * y + M[4], M[1] * x + M[3] * y + M[5]]; };
  var mod = function (a, n) { return ((a % n) + n) % n; };
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
  var esc = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
  var COARSE = window.matchMedia && matchMedia('(pointer: coarse)').matches;

  /* ---------- state ---------- */
  var state = {
    tile: { w: 400, h: 400, bg: '#FBF6EC', transparent: false },
    mode: 'grid',
    els: [],
    palette: ['#E98A74', '#A9C4D6', '#7F8B5A', '#F2B94B', '#2E2A22']
  };
  var assets = {};
  var uid = 1, sel = null, unit = 'in', previewing = false, zoomV = 70, engaged = false, started = false;

  function engage(action) {
    if (engaged) return;
    engaged = true;
    T('pattern_customized', { action: action, pieces: state.els.length, repeat: state.mode });
  }

  /* ---------- element helpers ---------- */
  function byId(id) { for (var i = 0; i < state.els.length; i++) if (state.els[i].id === id) return state.els[i]; return null; }
  function contentKey(el) {
    var a = assets[el.asset];
    if (a.kind === 'img') return el.asset;
    return el.tint ? el.asset + '|' + el.color : el.asset;
  }
  function markupForKey(k, exp) {
    var parts = k.split('|'), aid = parts[0], c = parts[1], a = assets[aid];
    var box = 'x="-50" y="' + f(-50 * a.ar) + '" width="100" height="' + f(100 * a.ar) + '" preserveAspectRatio="none"';
    if (!exp) { var px = proxyFor(k); if (px) return '<image href="' + px + '" ' + box + '/>'; }
    if (a.kind === 'img') return '<image ' + (exp ? 'xlink:href' : 'href') + '="' + a.url + '" ' + box + '/>';
    return c ? '<g fill="' + c + '" color="' + c + '">' + a.tint.split('__C__').join(c) + '</g>' : a.body;
  }
  function elT(el) { var s = el.size / 100; return 'translate(' + f(el.x) + ' ' + f(el.y) + ') rotate(' + f(el.rot) + ') scale(' + f(el.flip ? -s : s) + ' ' + f(s) + ')'; }
  function radius(el) { return el.size / 2 * Math.hypot(1, assets[el.asset].ar) * 1.08; }
  function maxR() { return state.els.reduce(function (m, e) { return Math.max(m, radius(e)); }, 0); }

  /* ---------- repeat geometry ---------- */
  function lattice() {
    var w = state.tile.w, h = state.tile.h;
    if (state.mode === 'halfdrop') return [[w, h / 2], [0, h]];
    if (state.mode === 'brick') return [[w, 0], [w / 2, h]];
    return [[w, 0], [0, h]];
  }
  // matrices that place the wrap copies of an element inside the tile
  function wrapMats() {
    var w = state.tile.w, h = state.tile.h, out = [[1, 0, 0, 1, 0, 0]], i, j;
    if (state.mode === 'mirror') {
      [-1, 0, 1].forEach(function (ix) { [-1, 0, 1].forEach(function (iy) {
        if (!ix && !iy) return;
        out.push([ix ? -1 : 1, 0, 0, iy ? -1 : 1, ix === 1 ? 2 * w : 0, iy === 1 ? 2 * h : 0]);
      }); });
      return out;
    }
    var L = lattice(), a = L[0], b = L[1];
    for (i = -2; i <= 2; i++) for (j = -2; j <= 2; j++) {
      if (!i && !j) continue;
      out.push([1, 0, 0, 1, i * a[0] + j * b[0], i * a[1] + j * b[1]]);
    }
    return out;
  }
  // tile placements covering a box v = [x0, y0, x1, y1]
  function placementsIn(v, pad) {
    var w = state.tile.w, h = state.tile.h, out = [], i, j;
    if (state.mode === 'mirror') {
      var i0 = Math.floor((v[0] - pad) / w) - 1, i1 = Math.ceil((v[2] + pad) / w) + 1;
      var j0 = Math.floor((v[1] - pad) / h) - 1, j1 = Math.ceil((v[3] + pad) / h) + 1;
      for (i = i0; i <= i1; i++) for (j = j0; j <= j1; j++) {
        var sx = mod(i, 2) ? -1 : 1, sy = mod(j, 2) ? -1 : 1;
        out.push([sx, 0, 0, sy, sx < 0 ? (i + 1) * w : i * w, sy < 0 ? (j + 1) * h : j * h]);
      }
      return out;
    }
    var L = lattice(), a = L[0], b = L[1];
    var K = Math.ceil(Math.max((v[2] - v[0]) / w, (v[3] - v[1]) / h)) +
            Math.ceil(Math.max(Math.abs(v[0]), Math.abs(v[1])) / Math.min(w, h)) + 3;
    for (i = -K; i <= K; i++) for (j = -2 * K; j <= 2 * K; j++) {
      var x = i * a[0] + j * b[0], y = i * a[1] + j * b[1];
      if (x + w < v[0] - pad || x > v[2] + pad || y + h < v[1] - pad || y > v[3] + pad) continue;
      out.push([1, 0, 0, 1, x, y]);
    }
    return out;
  }
  // keep an element's anchor inside the tile
  function normalize(el) {
    var w = state.tile.w, h = state.tile.h, k;
    if (state.mode === 'mirror') { el.x = clamp(el.x, 0, w); el.y = clamp(el.y, 0, h); return; }
    if (state.mode === 'halfdrop') { k = Math.floor(el.x / w); el.x -= k * w; el.y -= k * h / 2; el.y = mod(el.y, h); return; }
    if (state.mode === 'brick') { k = Math.floor(el.y / h); el.y -= k * h; el.x -= k * w / 2; el.x = mod(el.x, w); return; }
    el.x = mod(el.x, w); el.y = mod(el.y, h);
  }
  // smallest rectangle that repeats, and where the tile sits inside it
  function period() {
    var w = state.tile.w, h = state.tile.h, I = [1, 0, 0, 1, 0, 0];
    if (state.mode === 'halfdrop') return [2 * w, h, [I, [1, 0, 0, 1, w, h / 2], [1, 0, 0, 1, w, -h / 2]]];
    if (state.mode === 'brick') return [w, 2 * h, [I, [1, 0, 0, 1, w / 2, h], [1, 0, 0, 1, -w / 2, h]]];
    if (state.mode === 'mirror') return [2 * w, 2 * h, [I, [-1, 0, 0, 1, 2 * w, 0], [1, 0, 0, -1, 0, 2 * h], [-1, 0, 0, -1, 2 * w, 2 * h]]];
    return [w, h, [I]];
  }

  /* ---------- display proxies: heavy SVGs / big photos show as a light bitmap on screen; exports stay full quality ---------- */
  var PROXY_PX = 1024, proxies = new Map(), pending = new Set();
  function isHeavy(a) { return a.kind === 'img' ? a.big : (a.body.length > 120000 || (a.body.match(/</g) || []).length > 2500); }
  function proxyFor(k) {
    if (proxies.has(k)) return proxies.get(k);
    var parts = k.split('|'), a = assets[parts[0]];
    if (!a || !isHeavy(a) || pending.has(k)) return null;
    pending.add(k);
    buildProxy(a, parts[1]).then(function (url) { proxies.set(k, url); pending.delete(k); defsSig = null; render(); })
      .catch(function () { proxies.set(k, null); pending.delete(k); });
    return null;
  }
  function loadImg(url) { return new Promise(function (res, rej) { var im = new Image(); im.onload = function () { res(im); }; im.onerror = rej; im.src = url; }); }
  function buildProxy(a, color) {
    var pw = a.ar > 1 ? Math.round(PROXY_PX / a.ar) : PROXY_PX, ph = Math.round(pw * a.ar), src, revoke = null;
    if (a.kind === 'img') src = a.url;
    else {
      var doc = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="' + pw + '" height="' + ph +
        '" viewBox="-50 ' + f(-50 * a.ar) + ' 100 ' + f(100 * a.ar) + '">' + a.body + '</svg>';
      src = revoke = URL.createObjectURL(new Blob([doc], { type: 'image/svg+xml' }));
    }
    return loadImg(src).then(function (im) {
      var cv = document.createElement('canvas'); cv.width = pw; cv.height = ph;
      var x = cv.getContext('2d'); x.drawImage(im, 0, 0, pw, ph);
      if (color) { x.globalCompositeOperation = 'source-in'; x.fillStyle = color; x.fillRect(0, 0, pw, ph); }
      return new Promise(function (r) { cv.toBlob(r, 'image/png'); });
    }).then(function (blob) {
      if (revoke) URL.revokeObjectURL(revoke);
      return URL.createObjectURL(blob);
    });
  }

  /* ---------- history ---------- */
  var hist = [], fut = [], last = '';
  function snap() { return JSON.stringify({ tile: state.tile, mode: state.mode, els: state.els, palette: state.palette }); }
  function commit() {
    var s = snap(); if (s === last) return;
    hist.push(last); if (hist.length > 100) hist.shift(); fut.length = 0; last = s; syncHistBtns();
  }
  function restore(s) {
    var o = JSON.parse(s);
    state.tile = o.tile; state.mode = o.mode; state.els = o.els; state.palette = o.palette;
    sel = sel ? byId(sel.id) : null;
    syncAll(); render();
  }
  function undo() { if (!hist.length) return; fut.push(last); last = hist.pop(); restore(last); syncHistBtns(); }
  function redo() { if (!fut.length) return; hist.push(last); last = fut.pop(); restore(last); syncHistBtns(); }
  function syncHistBtns() { $('pm-undo').disabled = !hist.length; $('pm-redo').disabled = !fut.length; }

  /* ---------- rendering ---------- */
  var ed = $('pm-ed'), stage = $('pm-stage');
  var gDefs = $('pm-defs'), gGhost = $('pm-ghost'), gCopies = $('pm-copies'), gSel = $('pm-sel');
  var defsSig = null, keyId = new Map(), wm = [], raf = 0, patSig = '';
  var ZMIN = 1.25, ZMAX = 8;   // zoom 0..100 maps to 8 .. 1.25 tiles across the short side
  function viewSpan() { return ZMAX * Math.pow(ZMIN / ZMAX, zoomV / 100); }
  function setZoom(v) { zoomV = clamp(Math.round(v), 0, 100); render(); }
  // phones: start closer in so the tile you edit is bigger
  function defaultZoom() { return (stage.clientWidth || 999) < 600 ? 84 : 70; }
  var MODE_NAMES = { grid: 'Grid', halfdrop: 'Half-drop', brick: 'Brick', mirror: 'Mirror' };

  function render() { cancelAnimationFrame(raf); raf = requestAnimationFrame(doRender); }
  function updatePatterns() {
    var p = period(), sig = p[0] + ',' + p[1] + ',' + state.mode;
    if (sig === patSig) return;
    patSig = sig;
    var pt = $('pm-pat');
    pt.setAttribute('width', f(p[0])); pt.setAttribute('height', f(p[1]));
    pt.innerHTML = p[2].map(function (M) { return '<use href="#pm-tileart" transform="' + ms(M) + '"/>'; }).join('');
  }
  function doRender() {
    var w = state.tile.w, h = state.tile.h;
    var cw = ed.clientWidth || 1, ch = ed.clientHeight || 1;
    var span = viewSpan() * Math.max(w, h);
    var vw = cw >= ch ? span * cw / ch : span, vh = cw >= ch ? span : span * ch / cw;
    var vx = w / 2 - vw / 2, vy = h / 2 - vh / 2;
    ed.setAttribute('viewBox', f(vx) + ' ' + f(vy) + ' ' + f(vw) + ' ' + f(vh));
    var bg = state.tile.transparent ? 'none' : state.tile.bg;
    stage.classList.toggle('is-checker', state.tile.transparent);
    ['pm-tclipr', 'pm-tilebg', 'pm-tileborder'].forEach(function (id) { var r = $(id); r.setAttribute('width', w); r.setAttribute('height', h); });
    $('pm-tilebg').setAttribute('fill', bg);

    // shared content defs (rebuilt only when the set of looks changes)
    var keys = [], seen = {};
    state.els.forEach(function (el) { var k = contentKey(el); if (!seen[k]) { seen[k] = 1; keys.push(k); } });
    var sig = keys.join('\n');
    if (sig !== defsSig) {
      keyId = new Map();
      gDefs.innerHTML = keys.map(function (k, i) { keyId.set(k, 'pm-c' + i); return '<g id="pm-c' + i + '">' + markupForKey(k) + '</g>'; }).join('');
      defsSig = sig;
    }

    updatePatterns();
    if (previewing) {   // still view: draw every copy unclipped for a seam-free result
      var v = [vx, vy, vx + vw, vy + vh];
      gGhost.innerHTML = (state.tile.transparent ? '' : '<rect x="' + f(vx) + '" y="' + f(vy) + '" width="' + f(vw) + '" height="' + f(vh) + '" fill="' + bg + '"/>') +
        elementUses(placementsIn(v, maxR()), v);
    } else {
      gGhost.innerHTML = '<rect x="' + f(vx) + '" y="' + f(vy) + '" width="' + f(vw) + '" height="' + f(vh) + '" fill="url(#pm-pat)" opacity=".4"/>';
    }

    // the tile itself, with wrap copies, clipped
    wm = wrapMats();
    var cp = '';
    state.els.forEach(function (el) {
      var id = keyId.get(contentKey(el)), R = radius(el), t = elT(el), op = el.opacity < 1 ? ' opacity="' + el.opacity + '"' : '';
      wm.forEach(function (M, i) {
        var c = ap(M, el.x, el.y);
        if (c[0] < -R || c[0] > w + R || c[1] < -R || c[1] > h + R) return;
        cp += '<use href="#' + id + '" data-id="' + el.id + '" data-m="' + i + '" transform="' + ms(M) + ' ' + t + '"' + op + '/>';
      });
    });
    gCopies.innerHTML = cp;
    renderSel();
    $('pm-label').innerHTML = '<b>' + fmtLen(w) + ' × ' + fmtLen(h) + '</b>' + MODE_NAMES[state.mode] + ' repeat';
    $('pm-empty').hidden = state.els.length > 0 || previewing;
  }
  function elementUses(pl, v) {
    var s = '';
    state.els.forEach(function (el) {
      var id = keyId.get(contentKey(el)), R = radius(el), t = elT(el), op = el.opacity < 1 ? ' opacity="' + el.opacity + '"' : '';
      pl.forEach(function (P) {
        var c = ap(P, el.x, el.y);
        if (c[0] < v[0] - R || c[0] > v[2] + R || c[1] < v[1] - R || c[1] > v[3] + R) return;
        s += '<use href="#' + id + '" transform="' + ms(P) + ' ' + t + '"' + op + '/>';
      });
    });
    return s;
  }
  function pxUnit() {
    var vb = ed.viewBox.baseVal;
    return Math.max(vb.width / (ed.clientWidth || 1), vb.height / (ed.clientHeight || 1)) * (COARSE ? 1.5 : 1);
  }
  function renderSel() {
    if (!sel) { gSel.innerHTML = ''; return; }
    var a = assets[sel.asset], k = pxUnit();
    var bw = sel.size, bh = sel.size * a.ar, r = 7 * k, ry = -bh / 2 - 26 * k, un = 'rotate(' + f(-sel.rot) + ') scale(' + f(k) + ')';
    gSel.innerHTML = '<g transform="translate(' + f(sel.x) + ' ' + f(sel.y) + ') rotate(' + f(sel.rot) + ')">' +
      '<rect x="' + f(-bw / 2) + '" y="' + f(-bh / 2) + '" width="' + f(bw) + '" height="' + f(bh) + '" class="pm-selbox"/>' +
      '<line x1="0" y1="' + f(-bh / 2) + '" x2="0" y2="' + f(ry) + '" class="pm-selline"/>' +
      '<circle data-h="rot" cx="0" cy="' + f(ry) + '" r="' + f(r) + '" class="pm-handle"><title>Rotate</title></circle>' +
      '<rect data-h="scale" x="' + f(bw / 2 - r) + '" y="' + f(bh / 2 - r) + '" width="' + f(2 * r) + '" height="' + f(2 * r) + '" class="pm-handle"><title>Resize</title></rect>' +
      '<g data-h="del" class="pm-delh" transform="translate(' + f(-bw / 2) + ' ' + f(-bh / 2) + ') ' + un + '"><title>Delete</title>' +
        '<circle r="10"/><path d="M-3.8,-3.8L3.8,3.8M3.8,-3.8L-3.8,3.8"/></g>' +
      '<g data-h="move" class="pm-movh" transform="translate(' + f(-bw / 2) + ' ' + f(bh / 2) + ') ' + un + '"><title>Move</title>' +
        '<circle r="10"/><path d="M0,-6V6M-6,0H6"/><path class="tip" d="M0,-7.5l-2.6,2.8h5.2zM0,7.5l-2.6,-2.8h5.2zM-7.5,0l2.8,-2.6v5.2zM7.5,0l-2.8,-2.6v5.2z"/></g></g>';
  }

  /* ---------- units ---------- */
  function toDisp(u) { return unit === 'in' ? u / UPI : u / UPI * 25.4; }
  function fromDisp(d) { return unit === 'in' ? d * UPI : d / 25.4 * UPI; }
  function fmtLen(u) { return unit === 'in' ? (+(u / UPI).toFixed(2)) + ' in' : (+(u / UPI * 25.4).toFixed(1)) + ' mm'; }

  /* ---------- UI sync ---------- */
  function setSeg(id, value) {
    $(id).querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-pressed', String((b.value || b.dataset.mode) === value)); });
  }
  function syncTileUI() {
    setSeg('pm-unit', unit);
    var step = unit === 'in' ? 0.25 : 5;
    $('pm-tw').step = $('pm-th').step = step;
    $('pm-tw').value = +toDisp(state.tile.w).toFixed(unit === 'in' ? 2 : 1);
    $('pm-th').value = +toDisp(state.tile.h).toFixed(unit === 'in' ? 2 : 1);
    $('pm-bg').value = state.tile.bg;
    $('pm-trans').checked = state.tile.transparent;
    $('pm-modes').querySelectorAll('button').forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.mode === state.mode)); });
  }
  function syncSelUI() {
    $('pm-selempty').hidden = !!sel;
    $('pm-selpanel').hidden = !sel;
    var st = $('pm-selstep');
    st.classList.remove('is-img', 'is-svg', 'is-tint');
    if (!sel) return;
    var a = assets[sel.asset];
    if (a.kind === 'img') st.classList.add('is-img');
    if (a.kind === 'svg') st.classList.add('is-svg');
    if (a.kind === 'svg' && sel.tint) st.classList.add('is-tint');
    var size = $('pm-ssize'), rot = $('pm-srot'), op = $('pm-sop');
    size.max = Math.max(Math.round(2 * Math.max(state.tile.w, state.tile.h)), Math.ceil(sel.size));
    size.value = sel.size; $('pm-ssize-v').textContent = fmtLen(sel.size);
    rot.value = Math.round(sel.rot); $('pm-srot-v').textContent = Math.round(sel.rot) + '°';
    op.value = Math.round(sel.opacity * 100); $('pm-sop-v').textContent = Math.round(sel.opacity * 100) + '%';
    [size, rot, op].forEach(syncRange);
    $('pm-stint').checked = !!sel.tint;
    $('pm-scolor').value = sel.color;
  }
  function syncAll() { syncTileUI(); syncSelUI(); }
  function select(el) { sel = el; syncSelUI(); render(); }

  /* ---------- element ops ---------- */
  function addElement(asset, opts) {
    opts = opts || {};
    var w = state.tile.w, h = state.tile.h, a = assets[asset];
    var el = {
      id: 'e' + uid++, asset: asset,
      x: w / 2 + (Math.random() - .5) * w * .3, y: h / 2 + (Math.random() - .5) * h * .3,
      size: Math.min(w, h) * .3, rot: 0, flip: false,
      color: state.palette[state.els.length % state.palette.length] || '#333333',
      opacity: 1, tint: !!opts.tint
    };
    if (a.ar > 1) el.size /= a.ar; // keep tall images inside the tile
    state.els.push(el);
    commit(); select(el);
  }
  function removeEl(el) {
    state.els = state.els.filter(function (e) { return e !== el; });
    if (sel === el) sel = null;
    commit(); syncSelUI(); render(); engage('delete');
  }
  function dupEl(el) {
    var c = JSON.parse(JSON.stringify(el));
    c.id = 'e' + uid++; c.x = el.x + el.size * .35; c.y = el.y + el.size * .35;
    normalize(c);
    state.els.splice(state.els.indexOf(el) + 1, 0, c);
    commit(); select(c); engage('duplicate');
  }
  function moveZ(el, d) {
    var i = state.els.indexOf(el), j = clamp(i + d, 0, state.els.length - 1);
    if (i === j) return;
    state.els.splice(i, 1); state.els.splice(j, 0, el); commit(); render();
  }

  /* ---------- uploads ---------- */
  function readURL(file) { return new Promise(function (res, rej) { var r = new FileReader(); r.onload = function () { res(r.result); }; r.onerror = rej; r.readAsDataURL(file); }); }
  function readText(file) { return new Promise(function (res, rej) { var r = new FileReader(); r.onload = function () { res(String(r.result)); }; r.onerror = rej; r.readAsText(file); }); }
  var isSvg = function (file) { return /svg/i.test(file.type || '') || /\.svg$/i.test(file.name); };

  function addFiles(list, method) {
    var files = [].slice.call(list || []);
    if (!files.length) return;
    var ok = 0, types = {};
    // Still showing only the sample flowers? Their art replaces them (Undo brings them back).
    // Uploading on top of the sample was the biggest drop-off: about 1 in 5 who never cleared
    // it downloaded, vs half or more of those who cleared first (GA4, Oct 9-30 2026).
    var sampleIds = state.els.length && state.els.every(function (e) { return assets[e.asset] && assets[e.asset].builtin; })
      ? state.els.map(function (e) { return e.id; }) : null;
    var chain = Promise.resolve();
    files.forEach(function (file) {
      chain = chain.then(function () {
        if (isSvg(file)) return readText(file).then(function (t) { if (addSVG(t, file.name)) { ok++; types.svg = 1; } });
        if (/^image\//.test(file.type)) return addImage(file).then(function () { ok++; types[(file.type.split('/')[1] || 'img')] = 1; });
        toast('Skipped ' + file.name + '. Use PNG, JPG, WebP or SVG.');
        T('upload_error', { message: 'unsupported type', file_type: file.type || 'unknown' });
      }).catch(function () {
        toast('Couldn’t read ' + file.name + '.');
        T('upload_error', { message: 'read failed', file_type: file.type || 'unknown' });
      });
    });
    chain.then(function () {
      if (!ok) return;
      if (sampleIds) {
        state.els = state.els.filter(function (e) { return sampleIds.indexOf(e.id) < 0; });
        if (sel && sampleIds.indexOf(sel.id) >= 0) sel = null;
        commit(); syncSelUI(); render();
        toast('Your art replaced the sample flowers. Undo brings them back.');
      }
      T('file_upload', { method: method, files: ok, file_type: Object.keys(types).join(','), sample_removed: sampleIds ? 'yes' : 'no' });
      if (!started) { started = true; K('Started ' + TOOL_NAME, { files: ok }); }
      engage('upload');
    });
  }
  function svgFallbackClean(root, keepDataImages) {
    var drop = { script:1, foreignobject:1, iframe:1, embed:1, object:1, video:1, audio:1, animate:1,
                 animatemotion:1, animatetransform:1, animatecolor:1, set:1, discard:1, handler:1, listener:1, feimage:1 };
    if (!keepDataImages) drop.image = 1;
    [root].concat([].slice.call(root.querySelectorAll("*"))).forEach(function (n) {
      var ln = (n.localName || "").toLowerCase();
      if (drop[ln]) { n.remove(); return; }
      var scrub = function (css) { return String(css).replace(/@import[^;]*;?/gi, "").replace(/url\(\s*(?!["']?#)[^)]*\)/gi, "none"); };
      if (ln === "style") n.textContent = scrub(n.textContent);
      [].slice.call(n.attributes).forEach(function (a) {
        var l = (a.localName || "").toLowerCase(), v = String(a.value).replace(/[\u0000- ]+/g, "").toLowerCase();
        var keepImg = keepDataImages && ln === "image" && /^data:image\/(png|jpe?g|gif|webp|avif|bmp);/.test(v);
        if (/^on/i.test(a.name) || ((l === "href" || l === "src") && v.charAt(0) !== "#" && !keepImg) || (!keepImg && /^(javascript|vbscript|data):/.test(v))) n.removeAttributeNode(a);
        else if (/url\(/i.test(a.value)) { var c = scrub(a.value); if (c !== a.value) n.setAttribute(a.name, c); }
      });
    });
    return root;
  }
  function addSVG(text, name) {
    var doc = new DOMParser().parseFromString(
      window.BT && BT.cleanSvgText ? BT.cleanSvgText(text) : String(text).replace(/<!DOCTYPE[^[>]*(\[[\s\S]*?\])?\s*>/i, ""),
      "image/svg+xml");
    var r = doc.documentElement;
    if (doc.querySelector('parsererror') || r.nodeName.toLowerCase() !== 'svg') {
      toast('Couldn’t read that SVG. Try exporting it again.');
      T('upload_error', { message: 'invalid svg', file_type: 'svg' });
      return false;
    }
    if (window.BT && BT.sanitizeSvg) BT.sanitizeSvg(r, { keepDataImages: true });  // keeps embedded Canva pictures
    else svgFallbackClean(r, true);
    var vb = (r.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (vb.length !== 4 || vb.some(isNaN) || !vb[2] || !vb[3]) {
      vb = [0, 0, parseFloat(r.getAttribute('width')) || 100, parseFloat(r.getAttribute('height')) || 100];
    }
    var ar = vb[3] / vb[2];
    var keep = ['fill', 'stroke', 'stroke-width', 'style', 'opacity', 'fill-rule'];
    var rootAttrs = keep.filter(function (k) { return r.hasAttribute(k); }).map(function (k) { return k + '="' + esc(r.getAttribute(k)) + '"'; }).join(' ');
    var ser = new XMLSerializer();
    var inner = function (n) { return [].slice.call(n.childNodes).map(function (c) { return ser.serializeToString(c); }).join(''); };
    var tinted = r.cloneNode(true);
    var re = /(fill|stroke)\s*:\s*(?!none)(?!url)[^;}"]+/g;
    tinted.querySelectorAll('*').forEach(function (n) {
      ['fill', 'stroke'].forEach(function (at) { var v = n.getAttribute(at); if (v && v !== 'none' && v.indexOf('url(') !== 0) n.setAttribute(at, '__C__'); });
      var st = n.getAttribute('style'); if (st) n.setAttribute('style', st.replace(re, '$1:__C__'));
      if (n.nodeName.toLowerCase() === 'style') n.textContent = n.textContent.replace(re, '$1:__C__');
      if (n.nodeName.toLowerCase() === 'stop') n.setAttribute('stop-color', '__C__');
    });
    var wrap = function (c, attrs) {
      return '<svg x="-50" y="' + f(-50 * ar) + '" width="100" height="' + f(100 * ar) + '" viewBox="' + vb.join(' ') +
        '" preserveAspectRatio="none" overflow="visible"><g ' + attrs + '>' + c + '</g></svg>';
    };
    var id = 'a' + uid++;
    assets[id] = { kind: 'svg', ar: ar, name: name, body: wrap(inner(r), rootAttrs), tint: wrap(inner(tinted), 'fill="__C__"') };
    addElement(id, { tint: false });
    return true;
  }
  function addImage(file) {
    return readURL(file).then(function (url) {
      return loadImg(url).then(function (im) {
        var MAX = 2000, s = Math.min(1, MAX / Math.max(im.naturalWidth, im.naturalHeight));
        if (s >= 1) return { url: url, im: im };
        var c = document.createElement('canvas');
        c.width = Math.round(im.naturalWidth * s); c.height = Math.round(im.naturalHeight * s);
        c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
        var u2 = /jpe?g/.test(file.type) ? c.toDataURL('image/jpeg', .92) : c.toDataURL('image/png');
        return loadImg(u2).then(function (im2) { return { url: u2, im: im2 }; });
      });
    }).then(function (o) {
      var id = 'a' + uid++;
      assets[id] = { kind: 'img', ar: o.im.naturalHeight / o.im.naturalWidth, url: o.url, name: file.name, big: Math.max(o.im.naturalWidth, o.im.naturalHeight) > PROXY_PX };
      addElement(id);
    });
  }
  function knockWhite(el) {
    var a = assets[el.asset];
    return loadImg(a.url).then(function (im) {
      var c = document.createElement('canvas'); c.width = im.naturalWidth; c.height = im.naturalHeight;
      var x = c.getContext('2d'); x.drawImage(im, 0, 0);
      var d = x.getImageData(0, 0, c.width, c.height), p = d.data;
      for (var i = 0; i < p.length; i += 4) {
        var mn = Math.min(p[i], p[i + 1], p[i + 2]);
        if (mn > 238) p[i + 3] = 0; else if (mn > 208) p[i + 3] = Math.round(p[i + 3] * (238 - mn) / 30);
      }
      x.putImageData(d, 0, 0);
      var id = 'a' + uid++;
      assets[id] = { kind: 'img', ar: a.ar, url: c.toDataURL('image/png'), name: a.name, big: a.big };
      el.asset = id; commit(); render(); toast('White background removed'); engage('knockout');
    });
  }

  /* ---------- export ---------- */
  function svgDoc(vw, vh, dims, defs, body) {
    return '<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="' +
      dims[0] + '" height="' + dims[1] + '" viewBox="0 0 ' + f(vw) + ' ' + f(vh) + '"><defs>' + defs + '</defs>' + body + '</svg>';
  }
  function exportCopies(pairs) {
    var imgs = new Map(), s = '';
    pairs.forEach(function (pr) {
      var el = pr[0], t = pr[1], k = contentKey(el), a = assets[el.asset], op = el.opacity < 1 ? ' opacity="' + el.opacity + '"' : '';
      if (a.kind === 'img') {
        if (!imgs.has(k)) imgs.set(k, 'img' + imgs.size);
        s += '<use xlink:href="#' + imgs.get(k) + '" transform="' + t + '"' + op + '/>';
      } else s += '<g transform="' + t + '"' + op + '>' + markupForKey(k, true) + '</g>';
    });
    var defs = '';
    imgs.forEach(function (id, k) { defs += '<g id="' + id + '">' + markupForKey(k, true) + '</g>'; });
    return { s: s, defs: defs };
  }
  function tileSVG(dims) {
    var w = state.tile.w, h = state.tile.h, mats = wrapMats(), pairs = [];
    state.els.forEach(function (el) {
      var R = radius(el), t = elT(el);
      mats.forEach(function (M) { var c = ap(M, el.x, el.y); if (c[0] < -R || c[0] > w + R || c[1] < -R || c[1] > h + R) return; pairs.push([el, ms(M) + ' ' + t]); });
    });
    var o = exportCopies(pairs);
    var bg = state.tile.transparent ? '' : '<rect width="' + w + '" height="' + h + '" fill="' + state.tile.bg + '"/>';
    return svgDoc(w, h, dims || [f(w / UPI) + 'in', f(h / UPI) + 'in'],
      '<clipPath id="tile-clip"><rect width="' + w + '" height="' + h + '"/></clipPath>' + o.defs,
      bg + '<g clip-path="url(#tile-clip)">' + o.s + '</g>');
  }
  function sheetSize() {
    var v = $('pm-sheet').value;
    if (v === 'custom') return [Math.max(0.5, fromDisp(+$('pm-cw').value || 12) / UPI), Math.max(0.5, fromDisp(+$('pm-ch').value || 12) / UPI)];
    return v.split('x').map(Number);
  }
  function sheetScale() { return clamp(+$('pm-scale-pct').value || 100, 25, 300) / 100; }
  function sheetSVG(dims) {
    var sz = sheetSize(), W = sz[0] * UPI, H = sz[1] * UPI;
    var sc = sheetScale(), Wt = W / sc, Ht = H / sc;
    var v = [0, 0, Wt, Ht], pl = placementsIn(v, maxR()), pairs = [];
    state.els.forEach(function (el) {
      var R = radius(el), t = elT(el);
      pl.forEach(function (P) { var c = ap(P, el.x, el.y); if (c[0] < -R || c[0] > Wt + R || c[1] < -R || c[1] > Ht + R) return; pairs.push([el, ms(P) + ' ' + t]); });
    });
    var o = exportCopies(pairs);
    var bg = state.tile.transparent ? '' : '<rect width="' + f(W) + '" height="' + f(H) + '" fill="' + state.tile.bg + '"/>';
    return svgDoc(W, H, dims || [f(sz[0]) + 'in', f(sz[1]) + 'in'],
      '<clipPath id="sheet-clip"><rect width="' + f(W) + '" height="' + f(H) + '"/></clipPath>' + o.defs,
      bg + '<g clip-path="url(#sheet-clip)"><g transform="scale(' + f(sc) + ')">' + o.s + '</g></g>');
  }
  function saveBlob(blob, name) {
    var url = URL.createObjectURL(blob), a = document.createElement('a');
    a.href = url; a.rel = 'noopener';
    if ('download' in a) { a.download = (window.BT && BT.brandName ? BT.brandName(name) : name); document.body.appendChild(a); a.click(); a.remove(); }
    else window.open(url, '_blank');
    setTimeout(function () { URL.revokeObjectURL(url); }, 10000);
  }
  function crc32(buf) {
    var c = -1;
    for (var i = 0; i < buf.length; i++) { c ^= buf[i]; for (var k = 0; k < 8; k++) c = (c >>> 1) ^ (0xEDB88320 & -(c & 1)); }
    return (c ^ -1) >>> 0;
  }
  // writes a pHYs chunk so the PNG prints at true size
  function withDPI(blob, dpi) {
    return blob.arrayBuffer().then(function (ab) {
      var b = new Uint8Array(ab), ppm = Math.round(dpi / 0.0254);
      var chunk = new Uint8Array(21), dv = new DataView(chunk.buffer);
      dv.setUint32(0, 9); chunk.set([0x70, 0x48, 0x59, 0x73], 4); dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
      dv.setUint32(17, crc32(chunk.subarray(4, 17)));
      return new Blob([b.subarray(0, 33), chunk, b.subarray(33)], { type: 'image/png' });
    });
  }
  function dpiValue() { var b = $('pm-dpi').querySelector('[aria-pressed="true"]'); return +(b ? b.value : 300); }
  // ~16 MP keeps iPhones (canvas limit ~16.7 MP) happy
  var PNG_LIMIT = 16e6;
  function renderPNG(inW, inH, build) {
    var dpi = dpiValue(), pw = Math.round(inW * dpi), ph = Math.round(inH * dpi), note = '';
    if (pw * ph > PNG_LIMIT) {
      dpi = Math.floor(dpi * Math.sqrt(PNG_LIMIT / (pw * ph)));
      pw = Math.round(inW * dpi); ph = Math.round(inH * dpi);
      note = 'That sheet is large, so it was saved at ' + dpi + ' DPI.';
    }
    var url = URL.createObjectURL(new Blob([build([pw, ph])], { type: 'image/svg+xml' }));
    return loadImg(url).then(function (im) {
      var c = document.createElement('canvas'); c.width = pw; c.height = ph;
      c.getContext('2d').drawImage(im, 0, 0, pw, ph);
      return new Promise(function (r) { c.toBlob(r, 'image/png'); });
    }).then(function (blob) {
      URL.revokeObjectURL(url);
      if (!blob) throw new Error('PNG render failed');
      return withDPI(blob, dpi);
    }).then(function (blob) { return { blob: blob, dpi: dpi, note: note }; }, function (e) { URL.revokeObjectURL(url); throw e; });
  }
  function tileName(ext) { return 'pattern - ' + state.mode + ' tile - ' + (+(state.tile.w / UPI).toFixed(2)) + 'x' + (+(state.tile.h / UPI).toFixed(2)) + 'in - BA.' + ext; }
  function sheetName(ext) { var s = sheetSize(); return 'pattern - ' + state.mode + ' sheet - ' + (+s[0].toFixed(2)) + 'x' + (+s[1].toFixed(2)) + 'in - BA.' + ext; }

  var DL_BTNS = ['pm-dl-tpng', 'pm-dl-tsvg', 'pm-dl-spng', 'pm-dl-ssvg'];
  function setDlDisabled(on) { DL_BTNS.forEach(function (id) { $(id).disabled = on || !state.els.length; }); }

  function runDownload(kind) {
    if (!state.els.length) { toast('Add some art to your tile first.'); return; }
    if (previewing) setPreview(false);
    setDlDisabled(true);
    var isSheet = kind.indexOf('sheet') === 0, isPng = /png$/.test(kind), note = '';
    var props = function (extra) {
      var s = sheetSize();
      return Object.assign({ kind: kind, repeat: state.mode, pieces: state.els.length,
        tile_w_in: +(state.tile.w / UPI).toFixed(2), tile_h_in: +(state.tile.h / UPI).toFixed(2),
        sheet: isSheet ? s[0] + 'x' + s[1] : '', scale_pct: isSheet ? Math.round(sheetScale() * 100) : 100 }, extra || {});
    };
    var work = function () {
      var name = isSheet ? sheetName(isPng ? 'png' : 'svg') : tileName(isPng ? 'png' : 'svg');
      var p;
      if (isPng) {
        var sz = isSheet ? sheetSize() : [state.tile.w / UPI, state.tile.h / UPI];
        p = renderPNG(sz[0], sz[1], isSheet ? sheetSVG : tileSVG).then(function (o) { note = o.note; return { blob: o.blob, dpi: o.dpi }; });
      } else {
        p = Promise.resolve({ blob: new Blob([isSheet ? sheetSVG() : tileSVG()], { type: 'image/svg+xml' }), dpi: 0 });
      }
      return p.then(function (o) {
        saveBlob(o.blob, name);
        var pr = props({ file_name: name, dpi: o.dpi || '' });
        T('download_click', pr);
        K('Used ' + TOOL_NAME, pr);
      });
    };
    var stages = [
      { p: 22, wait: 300, text: 'Tiling your pattern…' },
      { p: 55, wait: 700, text: 'Matching the seams…' },
      { p: 84, wait: 700, text: isSheet ? 'Filling your sheet…' : 'Sizing your tile…' }
    ];
    if (window.BT && BT.runWithProgress) {
      BT.runWithProgress({
        stages: stages,
        gateText: 'Your pattern is almost ready! Enter your email to get it.',
        doneText: 'Your file is ready!',
        errorText: 'Something went wrong making your file. Try a smaller sheet or 150 DPI.',
        work: work,
        onEnd: function (r) { setDlDisabled(false); if (r.status === 'done' && note) toast(note); }
      }).then(function (r) { if (r.status === 'busy') setDlDisabled(false); });
    } else {
      work().catch(function () { toast('Something went wrong making your file.'); }).then(function () { setDlDisabled(false); });
    }
  }

  /* ---------- project save / open ---------- */
  function saveProject() {
    var used = {};
    Object.keys(assets).forEach(function (id) { if (!assets[id].builtin) used[id] = assets[id]; });
    saveBlob(new Blob([JSON.stringify({ v: 1, tool: 'seamless-pattern-maker', state: state, assets: used })], { type: 'application/json' }), 'pattern project - BA.json');
    T('project_save', { pieces: state.els.length });
  }
  // A project file can come from anyone, so nothing in it reaches the page unchecked:
  // SVG markup goes back through the sanitizer, pictures must be data: images, and
  // every number, colour and name is rebuilt from scratch.
  var HEX = /^#[0-9a-f]{3,8}$/i, ASSET_ID = /^[A-Za-z0-9_-]{1,40}$/;
  function num(v, lo, hi, d) { v = +v; return isFinite(v) ? clamp(v, lo, hi) : d; }
  function hex(v, d) { return typeof v === 'string' && HEX.test(v) ? v : d; }
  function cleanMarkup(markup) {
    var doc = new DOMParser().parseFromString('<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' + String(markup) + '</svg>', 'image/svg+xml');
    var r = doc.documentElement;
    if (doc.querySelector('parsererror') || r.nodeName.toLowerCase() !== 'svg') return null;
    if (window.BT && BT.sanitizeSvg) BT.sanitizeSvg(r, { keepDataImages: true }); else svgFallbackClean(r, true);
    var ser = new XMLSerializer();
    return [].slice.call(r.childNodes).map(function (c) { return ser.serializeToString(c); }).join('');
  }
  function cleanAsset(a) {
    if (!a || typeof a !== 'object') return null;
    var out = { ar: num(a.ar, 0.01, 100, 1), name: String(a.name || 'design').slice(0, 120) };
    if (a.kind === 'img') {
      if (typeof a.url !== 'string' || !/^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+\/=]+$/i.test(a.url)) return null;
      out.kind = 'img'; out.url = a.url; out.big = !!a.big; return out;
    }
    if (a.kind !== 'svg') return null;
    var body = cleanMarkup(a.body), tint = cleanMarkup(a.tint);
    if (body == null || tint == null) return null;
    out.kind = 'svg'; out.body = body; out.tint = tint; return out;
  }
  function cleanProject(o) {
    if (!o || !o.state || !Array.isArray(o.state.els)) throw new Error('bad file');
    var got = {};
    Object.keys(o.assets || {}).forEach(function (k) {
      if (!ASSET_ID.test(k) || (assets[k] && assets[k].builtin)) return;
      var a = cleanAsset(o.assets[k]); if (a) got[k] = a;
    });
    var have = function (k) { return got[k] || assets[k]; };
    var t = o.state.tile || {};
    var els = o.state.els.filter(function (e) { return e && have(e.asset); }).slice(0, 500).map(function (e, i) {
      return { id: 'e' + (i + 1), asset: e.asset, x: num(e.x, -1e5, 1e5, 0), y: num(e.y, -1e5, 1e5, 0), size: num(e.size, 1, 1e5, 100),
        rot: num(e.rot, -3600, 3600, 0), flip: !!e.flip, color: hex(e.color, '#333333'), opacity: num(e.opacity, 0, 1, 1), tint: !!e.tint };
    });
    var pal = Array.isArray(o.state.palette) ? o.state.palette.filter(function (c) { return typeof c === 'string' && HEX.test(c); }).slice(0, 12) : [];
    return {
      assets: got,
      state: { tile: { w: num(t.w, 10, 10000, 400), h: num(t.h, 10, 10000, 400), bg: hex(t.bg, '#FBF6EC'), transparent: !!t.transparent },
        mode: MODE_NAMES.hasOwnProperty(o.state.mode) ? o.state.mode : 'grid', els: els, palette: pal.length ? pal : state.palette }
    };
  }
  function openProject(file) {
    readText(file).then(function (txt) {
      var o = cleanProject(JSON.parse(txt));
      Object.keys(o.assets).forEach(function (k) { assets[k] = o.assets[k]; });
      state.tile = o.state.tile; state.mode = o.state.mode; state.els = o.state.els; state.palette = o.state.palette;
      var mx = 0;
      Object.keys(assets).concat(state.els.map(function (e) { return e.id; })).forEach(function (id) {
        var n = parseInt(String(id).replace(/^\D+/, ''), 10); if (n > mx) mx = n;
      });
      uid = mx + 1; sel = null; defsSig = null; patSig = '';
      commit(); syncAll(); render(); setDlDisabled(false);
      toast('Project opened');
      T('project_open', { pieces: state.els.length });
    }).catch(function () { toast('That isn’t a saved pattern. Pick the “pattern project-BAtools.json” file from Save my work.'); });
  }

  /* ---------- canvas interaction ---------- */
  function pt(e) { var p = ed.createSVGPoint(); p.x = e.clientX; p.y = e.clientY; var q = p.matrixTransform(ed.getScreenCTM().inverse()); return [q.x, q.y]; }
  var drag = null, wheelT = 0;
  ed.addEventListener('pointerdown', function (e) {
    if (previewing) return;
    var p = pt(e), hnd = e.target.closest('[data-h]');
    if (hnd && sel && hnd.dataset.h === 'del') { e.preventDefault(); removeEl(sel); return; }
    if (hnd && sel && hnd.dataset.h === 'move') { drag = { type: 'move', el: sel, start: p, ox: sel.x, oy: sel.y, M: [1, 0, 0, 1, 0, 0] }; ed.setPointerCapture(e.pointerId); e.preventDefault(); return; }
    if (hnd && sel) { drag = { type: hnd.dataset.h, el: sel }; ed.setPointerCapture(e.pointerId); e.preventDefault(); return; }
    var u = e.target.closest('use[data-id]');
    if (u) {
      var el = byId(u.dataset.id);
      if (sel !== el) select(el);
      drag = { type: 'move', el: el, start: p, ox: el.x, oy: el.y, M: wm[+u.dataset.m] || [1, 0, 0, 1, 0, 0] };
      ed.setPointerCapture(e.pointerId); e.preventDefault(); return;
    }
    if (sel) select(null);
  });
  ed.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var p = pt(e), el = drag.el;
    if (drag.type === 'move') {
      var dx = p[0] - drag.start[0], dy = p[1] - drag.start[1];
      el.x = drag.ox + drag.M[0] * dx; el.y = drag.oy + drag.M[3] * dy;
      drag.moved = drag.moved || Math.abs(dx) + Math.abs(dy) > 2;
    } else if (drag.type === 'scale') {
      el.size = Math.max(4, 2 * Math.hypot(p[0] - el.x, p[1] - el.y) / Math.hypot(1, assets[el.asset].ar));
      drag.moved = true;
    } else if (drag.type === 'rot') {
      var a = Math.atan2(p[0] - el.x, -(p[1] - el.y)) * 180 / Math.PI;
      if (e.shiftKey) a = Math.round(a / 15) * 15;
      el.rot = mod(a + 180, 360) - 180;
      drag.moved = true;
    }
    syncSelUI(); render();
  });
  function endDrag() {
    if (!drag) return;
    var d = drag; drag = null;
    normalize(d.el); commit(); syncSelUI(); render();
    if (d.moved) engage(d.type === 'move' ? 'move' : d.type === 'scale' ? 'resize' : 'rotate');
  }
  // The store theme listens for taps/clicks page-wide and errors on SVG pieces; keep those events in the canvas.
  ['touchstart', 'touchend', 'click', 'mousedown', 'mouseup'].forEach(function (t) {
    ed.addEventListener(t, function (e) { e.stopPropagation(); }, { passive: true });
  });
  ed.addEventListener('pointerup', endDrag);
  ed.addEventListener('pointercancel', endDrag);
  ed.addEventListener('wheel', function (e) {
    e.preventDefault();
    var u = e.target.closest('use[data-id]');
    if (previewing || !sel || !u || u.dataset.id !== sel.id) { setZoom(zoomV - e.deltaY * 0.05); return; }
    if (e.shiftKey) sel.rot = mod(sel.rot + (e.deltaY || e.deltaX) * 0.15 + 180, 360) - 180;
    else sel.size = Math.max(4, sel.size * Math.exp(-e.deltaY * 0.0015));
    syncSelUI(); render();
    clearTimeout(wheelT); wheelT = setTimeout(function () { commit(); engage('resize'); }, 350);
  }, { passive: false });

  function typing() {
    var a = document.activeElement;
    if (!a) return false;
    if (a.isContentEditable) return true;
    return /^(INPUT|SELECT|TEXTAREA)$/.test(a.tagName) && a.type !== 'range' && a.type !== 'checkbox' && a.type !== 'color';
  }
  document.addEventListener('keydown', function (e) {
    if (typing() || document.querySelector('.bt-proc')) return;
    var mod1 = e.ctrlKey || e.metaKey, key = (e.key || '').toLowerCase();
    if (!mod1 && !e.altKey && key === 'p') { setPreview(!previewing); return; }
    if (e.key === 'Escape' && previewing) { setPreview(false); return; }
    if (mod1 && key === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
    if (mod1 && key === 'y') { e.preventDefault(); redo(); return; }
    if (!sel) return;
    if (mod1 && key === 'd') { e.preventDefault(); dupEl(sel); return; }
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeEl(sel); return; }
    if (e.key === 'Escape') { select(null); return; }
    var st = e.shiftKey ? 10 : 1;
    var mv = { ArrowLeft: [-st, 0], ArrowRight: [st, 0], ArrowUp: [0, -st], ArrowDown: [0, st] }[e.key];
    if (mv) {
      e.preventDefault(); sel.x += mv[0]; sel.y += mv[1]; normalize(sel); render();
      clearTimeout(wheelT); wheelT = setTimeout(function () { commit(); engage('nudge'); }, 350);
    }
  });

  /* ---------- controls ---------- */
  ['pm-file', 'pm-file2'].forEach(function (id) {
    var inp = $(id);
    inp.addEventListener('change', function () { addFiles(inp.files, 'picker'); inp.value = ''; });
  });
  var dragDepth = 0;
  root.addEventListener('dragenter', function (e) { if (e.dataTransfer && [].indexOf.call(e.dataTransfer.types || [], 'Files') >= 0) { dragDepth++; root.classList.add('pm-dropping'); } });
  root.addEventListener('dragover', function (e) { e.preventDefault(); });
  root.addEventListener('dragleave', function () { dragDepth = Math.max(0, dragDepth - 1); if (!dragDepth) root.classList.remove('pm-dropping'); });
  root.addEventListener('drop', function (e) {
    e.preventDefault(); dragDepth = 0; root.classList.remove('pm-dropping');
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) addFiles(e.dataTransfer.files, 'drop');
  });

  $('pm-modes').addEventListener('click', function (e) {
    var b = e.target.closest('[data-mode]');
    if (!b || b.dataset.mode === state.mode) return;
    state.mode = b.dataset.mode; state.els.forEach(normalize); patSig = '';
    commit(); syncTileUI(); render();
    T('repeat_change', { repeat: state.mode }); engage('repeat');
  });
  $('pm-unit').addEventListener('bt:change', function (e) { unit = e.detail.value === 'mm' ? 'mm' : 'in'; syncAll(); render(); });
  $('pm-unit').addEventListener('click', function (e) {   // works even if the core's seg wiring isn't there
    var b = e.target.closest('button'); if (!b || window.BT) return;
    unit = b.value === 'mm' ? 'mm' : 'in'; syncAll(); render();
  });
  function tileSizeChanged(which) {
    var nw = fromDisp(+$('pm-tw').value), nh = fromDisp(+$('pm-th').value);
    if (!(nw >= 25) || !(nh >= 25) || nw > 4800 || nh > 4800) { toast('Pick a tile between 0.25 and 48 inches.'); syncTileUI(); return; }
    if ($('pm-square').checked) { if (which === 'w') nh = nw; else nw = nh; }
    var w = state.tile.w, h = state.tile.h;
    if ($('pm-scale').checked) {
      var rx = nw / w, ry = nh / h, rs = Math.sqrt(rx * ry);
      state.els.forEach(function (el) { el.x *= rx; el.y *= ry; el.size *= rs; });
    }
    state.tile.w = nw; state.tile.h = nh; state.els.forEach(normalize); patSig = '';
    commit(); syncAll(); render(); engage('tile_size');
  }
  $('pm-tw').addEventListener('change', function () { tileSizeChanged('w'); });
  $('pm-th').addEventListener('change', function () { tileSizeChanged('h'); });
  $('pm-bg').addEventListener('input', function (e) { state.tile.bg = e.target.value; render(); });
  $('pm-bg').addEventListener('change', function () { commit(); engage('background'); });
  $('pm-trans').addEventListener('change', function (e) { state.tile.transparent = e.target.checked; commit(); render(); engage('background'); });

  function selInput(id, fn) {
    var n = $(id);
    n.addEventListener('input', function () { if (!sel) return; fn(n); syncSelUI(); render(); });
    n.addEventListener('change', function () { commit(); engage('edit'); });
  }
  selInput('pm-ssize', function (n) { sel.size = +n.value; });
  selInput('pm-srot', function (n) { sel.rot = +n.value; });
  selInput('pm-sop', function (n) { sel.opacity = +n.value / 100; });
  selInput('pm-scolor', function (n) { sel.color = n.value; });
  $('pm-stint').addEventListener('change', function (e) { if (!sel) return; sel.tint = e.target.checked; commit(); syncSelUI(); render(); engage('recolor'); });
  $('pm-sflip').addEventListener('click', function () { if (!sel) return; sel.flip = !sel.flip; commit(); render(); engage('flip'); });
  $('pm-sdup').addEventListener('click', function () { if (sel) dupEl(sel); });
  $('pm-sdel').addEventListener('click', function () { if (sel) removeEl(sel); });
  $('pm-sfwd').addEventListener('click', function () { if (sel) moveZ(sel, 1); });
  $('pm-sback').addEventListener('click', function () { if (sel) moveZ(sel, -1); });
  $('pm-sdone').addEventListener('click', function () { select(null); });
  $('pm-sknock').addEventListener('click', function () { if (sel) knockWhite(sel).catch(function () { toast('Couldn’t process that image.'); }); });

  $('pm-sheet').addEventListener('change', function (e) {
    var c = e.target.value === 'custom';
    $('pm-custom').hidden = !c;
    if (c && !$('pm-cw').value) { $('pm-cw').value = +toDisp(12 * UPI).toFixed(2); $('pm-ch').value = +toDisp(12 * UPI).toFixed(2); }
  });
  function syncScale() { var r = $('pm-scale-pct'); $('pm-scale-pct-v').textContent = r.value + '%'; syncRange(r); }
  $('pm-scale-pct').addEventListener('input', syncScale);
  $('pm-dl-tpng').addEventListener('click', function () { runDownload('tile_png'); });
  $('pm-dl-tsvg').addEventListener('click', function () { runDownload('tile_svg'); });
  $('pm-dl-spng').addEventListener('click', function () { runDownload('sheet_png'); });
  $('pm-dl-ssvg').addEventListener('click', function () { runDownload('sheet_svg'); });
  $('pm-dpi').addEventListener('click', function (e) {   // fallback when the core's seg wiring isn't there
    var b = e.target.closest('button'); if (!b || window.BT) return; setSeg('pm-dpi', b.value);
  });

  $('pm-clear').addEventListener('click', function () {
    if (!state.els.length) { toast('Your tile is already empty.'); return; }
    var n = state.els.length;
    state.els = []; sel = null; commit(); syncSelUI(); render(); setDlDisabled(false);
    toast('Cleared. Undo brings it back.');
    T('clear_all', { pieces: n });
  });
  $('pm-undo').addEventListener('click', function () { undo(); setDlDisabled(false); });
  $('pm-redo').addEventListener('click', function () { redo(); setDlDisabled(false); });
  // The save/open project buttons are no longer on the page; wire them only if present.
  if ($('pm-save')) $('pm-save').addEventListener('click', saveProject);
  if ($('pm-open')) $('pm-open').addEventListener('change', function (e) { var fl = e.target.files[0]; if (fl) openProject(fl); e.target.value = ''; });

  function setPreview(on) {
    previewing = on;
    stage.classList.toggle('is-previewing', on);
    var b = $('pm-preview');
    b.classList.toggle('is-on', on);
    b.textContent = on ? 'Back to edit' : 'Preview';
    if (on) T('preview_click', { repeat: state.mode, pieces: state.els.length });
    render();
  }
  $('pm-preview').addEventListener('click', function () { setPreview(!previewing); });
  $('pm-zin').addEventListener('click', function () { setZoom(zoomV + 12); });
  $('pm-zout').addEventListener('click', function () { setZoom(zoomV - 12); });
  $('pm-zfit').addEventListener('click', function () { setZoom(defaultZoom()); });
  if (window.ResizeObserver) new ResizeObserver(render).observe(stage);
  else window.addEventListener('resize', render);

  // keep the download buttons in step with the element count (undo/redo/add/delete)
  var origCommit = commit;
  commit = function () { origCommit(); setDlDisabled(false); };

  /* ---------- starter pattern ---------- */
  var S = window.PM_STARTER;
  if (S && S.images && S.layout) {
    Object.keys(S.images).forEach(function (k) {
      var v = S.images[k];
      assets['start:' + k] = { kind: 'img', ar: v.ar, url: v.url, name: k + '.webp', builtin: true };
    });
    S.layout.forEach(function (r) {
      if (!assets['start:' + r[0]]) return;
      state.els.push({ id: 'e' + uid++, asset: 'start:' + r[0], x: r[1], y: r[2], size: r[3], rot: r[4], flip: !!r[5],
        color: state.palette[state.els.length % state.palette.length], opacity: 1, tint: false });
    });
  }
  zoomV = defaultZoom();
  last = snap();
  syncAll(); syncHistBtns(); syncScale(); setDlDisabled(false); render();
})();
